'use client';

/**
 * Purchase Flow Analytics
 *
 * Internal analytics context - consumers don't interact with this directly.
 * Just pass analyticsConfig and onAnalyticsEvent props to PurchaseFlow.
 */

import React, {
  createContext, useContext, useCallback, useMemo, useRef
} from 'react';
import type { PaymentOptionData } from '../types';
import type { AdditionalPurchaseInfo } from '../api';
import { getAdditionalPurchaseInfo } from '../api';
import type {
  AnalyticsEventName,
  AnalyticsAdditionalProperties,
  EventProperties,
  OnAnalyticsEvent,
  AnalyticsConfig,
  AnalyticsContextValue,
  PrevPaymentMethodSnapshot
} from '../types/analytics';

// Re-export types for external consumers
export type {
  AnalyticsEventName,
  AnalyticsAdditionalProperties,
  EventProperties,
  OnAnalyticsEvent,
  AnalyticsConfig,
  AnalyticsContextValue,
  PrevPaymentMethodSnapshot
};

// ============================================================================
// HELPERS
// ============================================================================

/** Payment method kind to analytics name mapping */
const paymentKindToAnalyticsName = (kind?: string): string | undefined => {
  const kindMap: Record<string, string> = {
    cc: 'credit card',
    pp: 'paypal',
    iap: 'IAP',
    ap: 'apple pay',
    am: 'apple merchant token',
    gp: 'google pay',
    none: 'none'
  };
  return kind ? kindMap[kind] : undefined;
};

/**
 * Derives the 'failed reason' fallback from the payment method kind when no API errorType
 * is available. Mirrors the old flow's getFailReason payment-kind branch.
 */
const getPaymentKindFailReason = (kind?: string): string | undefined => {
  const kindMap: Record<string, string> = {
    ap: 'apple pay failed',
    pp: 'paypal failed',
    cc: 'credit card failed',
    am: 'credit card failed',
    gp: 'google pay failed'
  };
  return kind ? kindMap[kind] : undefined;
};

/**
 * Convert object keys to lowercase spaced format for analytics.
 * Matches old flow's toLowerCaseSpaced(extraEventParams) behavior:
 *   snake_case → "snake case", camelCase → "camel case"
 */
const toSpacedLowerCase = (obj?: Record<string, string>): EventProperties => {
  if (!obj || typeof obj !== 'object') return {};
  const result: EventProperties = {};
  Object.keys(obj).forEach((key) => {
    const spacedKey = key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .toLowerCase();
    result[spacedKey] = obj[key];
  });
  return result;
};

/** Map additional purchase info to analytics event properties. Matches old purchaseEventAdditionalParams. */
const buildAdditionalPurchaseParams = (info: AdditionalPurchaseInfo): EventProperties => {
  const destination = info.spendingLimits?.length ? { destination: 'jumio' } : {};
  return {
    ...(info.paypalPayerId != null ? { 'paypal payer id': info.paypalPayerId } : {}),
    ...(info.paypalTransactionId != null ? { 'paypal transaction id': info.paypalTransactionId } : {}),
    ...(info.paypalBillingAgreementId != null ? { 'paypal billing agreement id': info.paypalBillingAgreementId } : {}),
    ...(info.paypalInvoiceId != null ? { 'paypal invoice id': info.paypalInvoiceId } : {}),
    ...(info.spendingLimits != null ? { 'spending limits exceeded': info.spendingLimits } : {}),
    ...destination
  };
};

// ============================================================================
// CONTEXT
// ============================================================================

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

// ============================================================================
// INTERNAL PROVIDER (used by PurchaseFlow, not exported)
// ============================================================================

interface ProviderProps {
  children: React.ReactNode;
  onAnalyticsEvent?: OnAnalyticsEvent;
  onTrackPageVisit?: (pageTitle: string) => void;
  config?: AnalyticsConfig;
}

export const AnalyticsProvider: React.FC<ProviderProps> = ({
  children,
  onAnalyticsEvent,
  onTrackPageVisit,
  config = {}
}) => {
  const onAnalyticsEventRef = useRef(onAnalyticsEvent);
  onAnalyticsEventRef.current = onAnalyticsEvent;
  const onTrackPageVisitRef = useRef(onTrackPageVisit);
  onTrackPageVisitRef.current = onTrackPageVisit;

  const configRef = useRef<AnalyticsConfig>(config);
  const paymentOptionRef = useRef<PaymentOptionData | null>(null);
  const retryCountRef = useRef(0);
  const conversionEventIdRef = useRef<string | undefined>(undefined);
  const extraEventParamsRef = useRef<Record<string, string> | undefined>(undefined);

  configRef.current = { ...configRef.current, ...config };

  /** Build common purchase event properties */
  const buildCommonProperties = useCallback((): EventProperties => {
    const cfg = configRef.current;
    const paymentOption = paymentOptionRef.current;

    // Calculate credit (price + bonus)
    const price = cfg.price || 0;
    const bonus = cfg.bonus || 0;
    const credit = (price + bonus).toFixed(2);

    const props: EventProperties = {
      // Core purchase properties
      'purchase screen': cfg.purchaseScreen,
      'click source': cfg.clickSource,
      credit,
      'base price': price.toFixed(2),
      'purchase amount': price.toFixed(2),

      // Payment method properties
      'payment method': paymentKindToAnalyticsName(paymentOption?.kind),
      'payment id': paymentOption?.id,
      ...(paymentOption?.ccType && { 'cc type': paymentOption.ccType.toLowerCase() }),
      'payment gateway': paymentOption?.gatewayName,

      // Purchase source
      'purchase source': cfg.fromMobileApp ? 'app' : 'web',

      // Retry count
      'retry count': retryCountRef.current,

      // PayPal billing agreement
      ...(paymentOption?.kind === 'pp' && cfg.hasPaypalBillingAgreement !== undefined
        ? { 'purchase billing agreement': cfg.hasPaypalBillingAgreement }
        : {}),

      // Purchase fees
      ...(cfg.purchaseFees !== undefined ? { 'purchase fees': cfg.purchaseFees.toFixed(2) } : {}),

      // Mobile app purchases count
      ...(cfg.fromMobileApp && cfg.purchasesCount !== undefined
        ? { 'purchases count': cfg.purchasesCount }
        : {}),

      // Advisor ID
      ...(cfg.advisorId ? { 'advisor id': cfg.advisorId } : {}),

      // Continued session
      ...(cfg.continuedSession ? { 'continued session': cfg.continuedSession } : {}),

      // Additional purchase props (flow, pay as you go)
      ...(cfg.flow !== undefined && { flow: cfg.flow }),
      ...(cfg.isPayg !== undefined && { 'pay as you go used': cfg.isPayg }),

      // new flow flag
      wallet26: true
    };

    return props;
  }, []);

  /** Build additionalProperties for Facebook Pixel / Conversion API */
  const buildConversionProps = useCallback((
    extra?: Partial<AnalyticsAdditionalProperties>
  ): AnalyticsAdditionalProperties => {
    const conversionEventId = conversionEventIdRef.current;
    return { ...(conversionEventId ? { conversionEventId } : {}), ...extra };
  }, []);

  /** Build prev payment method props from config (matches old currentPaymentMethodData()). Caller must set config.prevPaymentMethod before tracking. */
  const buildPrevPaymentMethodProps = useCallback((): EventProperties => {
    const src = configRef.current.prevPaymentMethod;
    if (!src) return {};
    return {
      'prev payment method': paymentKindToAnalyticsName(src.kind),
      'prev payment id': src.id,
      'prev payment gateway': src.gatewayName
    };
  }, []);

  /** Build available methods (matches old noPaymentMethodsEventData(options): always has 'available methods', value undefined or joined string). */
  const buildAvailableMethodsProps = useCallback((): EventProperties => {
    const { availableMethods } = configRef.current;
    if (!availableMethods || availableMethods.length === 0) {
      return { 'available methods': undefined };
    }
    const methodNames = availableMethods.map(k => paymentKindToAnalyticsName(k)).filter(Boolean);
    return { 'available methods': methodNames.join(' + ') };
  }, []);

  /** Common flow-level properties shared across non-purchase events. */
  const buildFlowProps = useCallback((): EventProperties => {
    const { flow, isPayg } = configRef.current;
    return {
      ...(flow !== undefined && { flow }),
      ...(isPayg !== undefined && { 'pay as you go used': isPayg }),
      wallet26: true
    };
  }, []);

  /** Send event with exactly the properties passed (no automatic merge). Each tracker builds the full payload to match old implementation. */
  const trackEvent = useCallback((
    eventName: AnalyticsEventName,
    properties: EventProperties = {},
    extraConversionProps?: Partial<AnalyticsAdditionalProperties>
  ) => {
    const cleanProperties = Object.fromEntries(
      Object.entries(properties).filter(([, v]) => v !== undefined)
    );

    const additionalProperties = buildConversionProps(extraConversionProps);

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[Analytics]', eventName, cleanProperties, additionalProperties);
    }

    onAnalyticsEventRef.current?.(eventName, cleanProperties, additionalProperties);
  }, []);

  const updateConfig = useCallback((newConfig: Partial<AnalyticsConfig>) => {
    configRef.current = { ...configRef.current, ...newConfig };
  }, []);

  const setPaymentOption = useCallback((option: PaymentOptionData | null) => {
    paymentOptionRef.current = option;
  }, []);

  const setConversionEventId = useCallback((id?: string) => {
    conversionEventIdRef.current = id;
  }, []);

  const setExtraEventParams = useCallback((params?: Record<string, string>) => {
    extraEventParamsRef.current = params;
  }, []);

  // ---- Purchase Events ----

  const trackPurchaseStarted = useCallback(() => {
    const localCurrency = configRef.current.displayCurrency;
    trackEvent('purchase started', {
      ...buildCommonProperties(),
      ...(localCurrency && { 'local currency': localCurrency }),
      ...(configRef.current.fxRate && { 'fx rate used': configRef.current.fxRate }),
      ...(configRef.current.localAmount && { 'local amount': configRef.current.localAmount.toFixed(2) })
    });
  }, [trackEvent, buildCommonProperties, buildConversionProps]);

  const trackPurchaseCompleted = useCallback((purchaseURL?: string, extraProps?: EventProperties) => {
    const cfg = configRef.current;
    const credit = ((cfg.price || 0) + (cfg.bonus || 0)).toFixed(2);
    const localCurrency = cfg.displayCurrency;

    retryCountRef.current = 0;

    const purchaseProperties: EventProperties = {
      ...buildCommonProperties(),
      ...(localCurrency && { 'local currency': localCurrency }),
      price: (cfg.price ?? 0).toFixed(2),
      currency: 'USD',
      'transaction total': credit,
      ...(cfg.fxRate && { 'fx rate used': cfg.fxRate }),
      ...(cfg.localAmount && { 'local amount': cfg.localAmount.toFixed(2) }),
      ...toSpacedLowerCase(extraEventParamsRef.current),
      ...extraProps
    };

    const trackPurchaseEvents = (additionalParams?: EventProperties) => {
      const allParams = { ...purchaseProperties, ...additionalParams };
      trackEvent('Purchase', allParams);
      if (cfg.purchasesCount === 0) {
        trackEvent('first purchase', allParams, { isCustomEvent: true });
      }
    };

    if (!purchaseURL) {
      trackPurchaseEvents();
      return;
    }

    getAdditionalPurchaseInfo(purchaseURL)
      .then((res) => {
        trackPurchaseEvents(buildAdditionalPurchaseParams(res));
      })
      .catch(() => {
        trackPurchaseEvents();
      });
  }, [trackEvent, buildCommonProperties, buildConversionProps]);

  /**
   * Track purchase failed event
   * Matches mpTrackPurchaseFailedEvent in purchases_utils.js
   *
   * All events get full context from buildCommonProperties() (via trackEvent merge):
   * purchase screen, click source, credit, payment method, retry count, etc.,
   * plus conversionEventId when set. For 'user canceled' no errorProps are needed.
   *
   * @param reason - Failed reason (e.g., 'api error', 'user canceled')
   * @param errorProps - Optional additional properties:
   *   - 'payment error message': Error message from API
   *   - 'failed reason description': errorName from API
   *   - 'failed reason error code': errorCode from API
   *   - 'spending limits exceeded': spendingLimits array
   *   - 'destination': 'jumio' if spending limits
   */
  const trackPurchaseFailed = useCallback((reason: string, errorProps?: EventProperties) => {
    const cfg = configRef.current;
    const { topupFlow, displayCurrency } = cfg;
    const paymentOption = paymentOptionRef.current;
    const isCreditCard = paymentOption?.kind === 'cc' || paymentOption?.kind === 'am';

    // When only a generic 'api error' falls through, apply the payment-kind fallback
    // ('apple pay failed', 'paypal failed', etc.) to mirror the old flow's getFailReason logic.
    const effectiveReason = reason === 'api error'
      ? (getPaymentKindFailReason(paymentOption?.kind) ?? reason)
      : reason;

    trackEvent('purchase failed', {
      ...buildCommonProperties(),
      'failed reason': effectiveReason,
      isCreditCard,
      'payment gateway': paymentOption?.gatewayName,
      ...(displayCurrency && { 'local currency': displayCurrency }),
      ...(cfg.fxRate && { 'fx rate used': cfg.fxRate }),
      ...(cfg.localAmount && { 'local amount': cfg.localAmount.toFixed(2) }),
      ...(topupFlow && { 'top up flow': topupFlow }),
      ...toSpacedLowerCase(extraEventParamsRef.current),
      ...errorProps
    });
  }, [trackEvent, buildCommonProperties]);

  // ---- 3DS Events ----

  const track3DSRequired = useCallback(() => {
    trackEvent('purchase 3DS required', buildCommonProperties());
  }, [trackEvent, buildCommonProperties]);

  const track3DSFailed = useCallback((error?: string) => {
    trackEvent('purchase 3DS failed', {
      ...buildCommonProperties(),
      'failed reason': error || '3DS authentication failed',
      'ThreeDS Auth Result': 'failed'
    });
  }, [trackEvent, buildCommonProperties]);

  // ---- Add Card Events ----

  /**
   * Track "add new card draw" page visit. Call this from the payment sources screen only
   * when opening the add-card flow (before trackStartAddingCard). No-op if onTrackPageVisit not provided.
   */
  const trackAddNewCardDrawPageVisit = useCallback(() => {
    onTrackPageVisitRef.current?.('add new card draw');
  }, []);

  /**
   * Track start adding card event.
   * Old: noPaymentMethodsEventData(options), currentPaymentMethodData() (prev payment method/id/gateway), purchase screen, aditionalPurchaseProps().
   * Pass prevPaymentMethod when the caller has the current default (so prev props are sent); we also store it in config for trackCardAdded later.
   */
  const trackStartAddingCard = useCallback((
    kind = 'cc',
    prevPaymentMethod?: PrevPaymentMethodSnapshot
  ) => {
    const { purchaseScreen } = configRef.current;
    const eventName = kind === 'cc' ? 'add credit card started' : 'add apple pay started';

    paymentOptionRef.current = { kind };

    if (prevPaymentMethod) {
      configRef.current = { ...configRef.current, prevPaymentMethod };
    }
    const prevProps = prevPaymentMethod
      ? {
        'prev payment method': paymentKindToAnalyticsName(prevPaymentMethod.kind),
        'prev payment id': prevPaymentMethod.id,
        'prev payment gateway': prevPaymentMethod.gatewayName
      }
      : buildPrevPaymentMethodProps();

    trackEvent(eventName, {
      ...buildAvailableMethodsProps(),
      ...prevProps,
      ...(purchaseScreen && { 'purchase screen': purchaseScreen }),
      ...buildFlowProps()
    });
  }, [trackEvent, buildAvailableMethodsProps, buildPrevPaymentMethodProps, buildFlowProps]);

  /**
   * Track card added event.
   * Old: purchase screen, params, currentPaymentMethodData() (prev payment method/id/gateway), noPaymentMethodsEventData(options), aditionalPurchaseProps().
   * Prev payment comes from config (set when trackStartAddingCard was called with prevPaymentMethod) or pass explicitly.
   */
  const trackCardAdded = useCallback((
    paymentId: number,
    kind = 'cc',
    ccType?: string,
    prevPaymentMethod?: PrevPaymentMethodSnapshot
  ) => {
    const { purchaseScreen } = configRef.current;
    const eventName = kind === 'cc' ? 'credit card added' : 'apple pay added';
    const prevProps = prevPaymentMethod
      ? {
        'prev payment method': paymentKindToAnalyticsName(prevPaymentMethod.kind),
        'prev payment id': prevPaymentMethod.id,
        'prev payment gateway': prevPaymentMethod.gatewayName
      }
      : buildPrevPaymentMethodProps();

    trackEvent(
      eventName,
      {
        ...(purchaseScreen && { 'purchase screen': purchaseScreen }),
        'payment id': paymentId,
        ...(ccType && { 'cc type': ccType.toLowerCase() }),
        ...prevProps,
        ...buildAvailableMethodsProps(),
        ...buildFlowProps()
      },
      { isCustomEvent: true }
    );
  }, [trackEvent, buildPrevPaymentMethodProps, buildAvailableMethodsProps, buildFlowProps]);

  /**
   * Track add card failed event.
   * Old: cc type, paramOrNul('failed reason error code', errorCode), paramOrNul('error message', error), paramOrNul('failed reason description', errorName), paramOrNul('failed reason', failedReason), aditionalPurchaseProps().
   * errorProps can include: 'failed reason error code', 'error message', 'failed reason description' (same keys as old).
   */
  const trackAddCardFailed = useCallback((reason: string, errorProps?: EventProperties) => {
    const paymentOption = paymentOptionRef.current;
    const eventName = paymentOption?.kind === 'am' ? 'add apple pay failed' : 'add credit card failed';

    trackEvent(eventName, {
      ...(paymentOption?.ccType && { 'cc type': paymentOption.ccType.toLowerCase() }),
      'failed reason': reason,
      ...buildFlowProps(),
      ...errorProps
    });
  }, [trackEvent, buildFlowProps]);

  // ---- Payment Method Events ----

  /** Old: only 'chosen payment method'. */
  const trackPaymentMethodClicked = useCallback((kind: string) => {
    trackEvent('payment method clicked', {
      'chosen payment method': paymentKindToAnalyticsName(kind),
      ...buildFlowProps()
    });
  }, [trackEvent, buildFlowProps]);

  /**
   * Track payment method changed event.
   * Old: properties, currentPaymentMethodData(), purchase screen, aditionalPurchaseProps(). Caller must set config.prevPaymentMethod before calling.
   */
  const trackPaymentMethodChanged = useCallback((kind: string, paymentId: number, ccType?: string) => {
    const { purchaseScreen } = configRef.current;
    trackEvent('payment method changed', {
      'payment method': paymentKindToAnalyticsName(kind),
      'payment id': paymentId,
      ...(ccType && { 'cc type': ccType.toLowerCase() }),
      ...buildPrevPaymentMethodProps(),
      'purchase screen': purchaseScreen ?? 'payment methods',
      ...buildFlowProps()
    });
  }, [trackEvent, buildPrevPaymentMethodProps, buildFlowProps]);

  /** Old: deleted payment method, default payment method, num of payment methods, default payment method removed, aditionalPurchaseProps(). */
  const trackPaymentMethodRemoved = useCallback((
    deletedKind: string,
    defaultPaymentMethodKind: string,
    numPaymentMethods: number,
    defaultPaymentMethodRemoved: boolean
  ) => {
    trackEvent('payment method removed', {
      'deleted payment method': paymentKindToAnalyticsName(deletedKind),
      'default payment method': paymentKindToAnalyticsName(defaultPaymentMethodKind),
      'num of payment methods': numPaymentMethods,
      'default payment method removed': defaultPaymentMethodRemoved,
      ...buildFlowProps()
    });
  }, [trackEvent, buildFlowProps]);

  /**
   * Track payment sources screen event.
   * Old: purchase screen, default payment method, number of credit cards, aditionalPurchaseProps().
   * When purchaseScreen is omitted (e.g. from purchase flow's PaymentMethodsListScreen), uses config.
   */
  const trackPaymentSourcesScreen = useCallback((
    defaultPaymentMethodKind: string,
    numberOfCreditCards: number,
    purchaseScreen?: string
  ) => {
    const { purchaseScreen: configPurchaseScreen } = configRef.current;
    const screen = purchaseScreen ?? configPurchaseScreen ?? '';
    trackEvent('payment sources screen', {
      'purchase screen': screen,
      'default payment method': paymentKindToAnalyticsName(defaultPaymentMethodKind),
      'number of credit cards': numberOfCreditCards,
      ...buildFlowProps()
    });
  }, [trackEvent, buildFlowProps]);

  /**
   * Track add funds screen view. Old: trackMPEventAdditionalFundsRequired (payment_options.js).
   * Caller passes credit, base price, and payment data; we merge purchase screen, flow, pay as you go from config.
   */
  const trackAddFundsScreen = useCallback((params: EventProperties) => {
    const { purchaseScreen } = configRef.current;
    trackEvent('add funds screen', {
      ...params,
      ...buildAvailableMethodsProps(),
      ...(purchaseScreen && { 'purchase screen': purchaseScreen }),
      ...buildFlowProps()
    });
  }, [trackEvent, buildAvailableMethodsProps, buildFlowProps]);

  const incrementRetryCount = useCallback(() => { retryCountRef.current += 1; }, []);
  const resetRetryCount = useCallback(() => { retryCountRef.current = 0; }, []);

  const value = useMemo<AnalyticsContextValue>(() => ({
    trackEvent, updateConfig, setPaymentOption, setConversionEventId, setExtraEventParams,
    trackPurchaseStarted, trackPurchaseCompleted, trackPurchaseFailed,
    track3DSRequired, track3DSFailed,
    trackAddNewCardDrawPageVisit, trackStartAddingCard, trackCardAdded, trackAddCardFailed,
    trackPaymentMethodClicked, trackPaymentMethodChanged, trackPaymentMethodRemoved, trackPaymentSourcesScreen,
    trackAddFundsScreen,
    incrementRetryCount, resetRetryCount
  }), [
    trackEvent, updateConfig, setPaymentOption, setConversionEventId, setExtraEventParams,
    trackPurchaseStarted, trackPurchaseCompleted, trackPurchaseFailed,
    track3DSRequired, track3DSFailed,
    trackAddNewCardDrawPageVisit, trackStartAddingCard, trackCardAdded, trackAddCardFailed,
    trackPaymentMethodClicked, trackPaymentMethodChanged, trackPaymentMethodRemoved, trackPaymentSourcesScreen,
    trackAddFundsScreen,
    incrementRetryCount, resetRetryCount
  ]);

  return (
    <AnalyticsContext.Provider value={ value }>
      {children}
    </AnalyticsContext.Provider>
  );
};

// ============================================================================
// HOOK (for internal components)
// ============================================================================

/**
 * Hook for tracking analytics in purchase flow components.
 * Returns no-op functions if analytics not configured.
 */
export const usePurchaseAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    const noop = () => { };
    return {
      trackEvent: noop, updateConfig: noop, setPaymentOption: noop,
      setConversionEventId: noop, setExtraEventParams: noop,
      trackPurchaseStarted: noop, trackPurchaseCompleted: noop, trackPurchaseFailed: noop,
      track3DSRequired: noop, track3DSFailed: noop,
      trackAddNewCardDrawPageVisit: noop, trackStartAddingCard: noop, trackCardAdded: noop, trackAddCardFailed: noop,
      trackPaymentMethodClicked: noop, trackPaymentMethodChanged: noop, trackPaymentMethodRemoved: noop, trackPaymentSourcesScreen: noop,
      trackAddFundsScreen: noop,
      incrementRetryCount: noop, resetRetryCount: noop
    };
  }

  return context;
};
